import {
  selectIsWhatsAppInfobipLocalesEnabled,
  selectNextBestActionEnabled,
} from 'core/userContext';
import isNil from 'lodash/isNil';
import { useIsModernizedSegmentationPageEnabled } from 'modules/segmentation/hooks';
import { NBAListIndication } from 'modules/ai/nba/components/NBAListIndication';
import { useCampaignContext, useTemplateContext } from 'modules/campaigns/editor/context';
import { getRecipientsDisplayText } from 'modules/campaigns/editor/utils';
import { ExpectedSendSize, useCampaignSendSize } from 'modules/campaigns/lib/hooks';
import { AudienceList } from 'modules/domains/campaign';
import { useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Link } from 'ui/links';
import { Skeleton } from 'ui/skeleton';
import { colors } from 'ui/theme';
import { HelpToggletip, TipPlacement, Tooltip } from 'ui/tooltip';

import { CampaignFormField, ReadOnlyField } from '../../FormFields';
import {
  LightText,
  NewLineSeparatedListItem,
  SectionHeader,
  SectionWrapper,
  SpaceSeparatedList,
} from '../Sections.styled';
import { WhatsAppPrimaryContent } from 'modules/campaigns/editor/types';
import { TemplateType } from 'constants/templateTypes';
import { WhatsAppSendLimit } from 'modules/graphql';
import { WHATSAPP_SEND_LIMIT_SHORT_TEXT_MAPPER } from 'modules/campaigns/editor/constants';
import { WhatsApp } from 'constants/messageMediums';

const StyledCampaignFormField = styled(CampaignFormField)`
  label {
    display: inline-flex;
    gap: 7.5px;
  }
`;

export const AUDIENCE_SECTION_TEST_ID = 'audience-section';

type RecipientsProps = { sendSize: ExpectedSendSize; hasError: boolean; isEstimated: boolean };
const Recipients = ({ sendSize, hasError, isEstimated }: RecipientsProps) => {
  const recipientsContent = isNil(sendSize) ? (
    <LightText>{getRecipientsDisplayText(0, false)}</LightText>
  ) : (
    <>{getRecipientsDisplayText(sendSize, isEstimated)}</>
  );

  return (
    <ReadOnlyField name="recipients">
      {hasError ? 'Could not calculate total recipients' : recipientsContent}
    </ReadOnlyField>
  );
};

export const AudienceSection = () => {
  const { estimatedSendSizeData, expectedSendSizeData, campaign } = useCampaignContext();
  const { sendSize, isLoading, isFetching, hasError, isEstimated } = useCampaignSendSize({
    estimatedSendSizeData,
    expectedSendSizeData,
  });
  const isWhatsAppGAEnabled = useSelector(selectIsWhatsAppInfobipLocalesEnabled);
  const { templateAndContent } = useTemplateContext();
  const { primaryContent, templateType } = templateAndContent || {};
  const sendLimit = (primaryContent as WhatsAppPrimaryContent)?.whatsAppSender?.sendLimit;
  const isSendLimitShown =
    isWhatsAppGAEnabled &&
    templateType === TemplateType.BusinessInitiated &&
    campaign.messageMedium === WhatsApp &&
    !!sendLimit &&
    sendLimit !== WhatsAppSendLimit.LimitNa;

  const nbaEnabled = useSelector(selectNextBestActionEnabled);
  const isModernizedSegmentationPageEnabled = useIsModernizedSegmentationPageEnabled();

  const audienceLists = useWatch({ name: 'audienceLists' });
  const { useSendLists, useSuppressionLists, sendLists, suppressionLists } = audienceLists;

  return (
    <SectionWrapper data-test={AUDIENCE_SECTION_TEST_ID}>
      <SectionHeader>Audience</SectionHeader>
      <StyledCampaignFormField
        labelText={
          <>
            Recipients
            <HelpToggletip
              tooltipContent="Calculated by combining users in selected lists, less those in suppression lists and those who've unsubscribed to the message type or channel of the template."
              iconProps={{
                color: colors.secondaryText,
              }}
              placement={TipPlacement.Right}
            />
          </>
        }
      >
        {/* Show loading when initially fetching (`isLoading`) or when refetching (`isFetching`) */}
        {isLoading || isFetching ? (
          <Skeleton height={20} width={200} />
        ) : (
          <Recipients sendSize={sendSize} hasError={hasError} isEstimated={isEstimated} />
        )}
      </StyledCampaignFormField>
      <CampaignFormField labelText="Send list">
        <ReadOnlyField name="sendLists">
          <SpaceSeparatedList>
            {useSendLists ? (
              sendLists.map(({ name, id }: AudienceList) => (
                <NewLineSeparatedListItem key={id}>
                  <Link
                    hasOpenNewTabIcon
                    href={`/segmentation?emailListId=${id}`}
                    useReactRouter={isModernizedSegmentationPageEnabled}
                  >
                    {name}
                  </Link>
                  {/* Attached AI icon indicates AI created and targeted list based on naming convention */}
                  {nbaEnabled && <NBAListIndication name={name} />}
                </NewLineSeparatedListItem>
              ))
            ) : (
              <LightText>Not set</LightText>
            )}
          </SpaceSeparatedList>
        </ReadOnlyField>
      </CampaignFormField>
      <CampaignFormField labelText="Suppress list">
        <ReadOnlyField name="suppressionLists">
          <SpaceSeparatedList>
            {useSuppressionLists ? (
              suppressionLists.map(({ name, id }: AudienceList) => (
                <NewLineSeparatedListItem key={id}>
                  <Tooltip isEnabled tooltipContent={name}>
                    <Link
                      hasOpenNewTabIcon
                      href={`/segmentation?emailListId=${id}`}
                      useReactRouter={isModernizedSegmentationPageEnabled}
                    >
                      {name}
                    </Link>
                  </Tooltip>
                </NewLineSeparatedListItem>
              ))
            ) : (
              <LightText>Not set</LightText>
            )}
          </SpaceSeparatedList>
        </ReadOnlyField>
      </CampaignFormField>
      {isSendLimitShown && (
        <CampaignFormField labelText="Sending limit">
          <ReadOnlyField name="sendingLimit">
            {WHATSAPP_SEND_LIMIT_SHORT_TEXT_MAPPER[sendLimit]} business-initiated conversations in
            24 hour window
          </ReadOnlyField>
        </CampaignFormField>
      )}
    </SectionWrapper>
  );
};
